import React from 'react';
import { ZapIcon, ThermometerSnowflakeIcon, ShieldIcon, FingerprintIcon, MapPinIcon } from 'lucide-react';
export function DataCenterSection() {
  const features = [{
    icon: <ZapIcon className="w-6 h-6" data-id="element-203" />,
    title: 'Redundant Power',
    description: '2N power architecture with dual utility feeds and enterprise-grade UPS systems.'
  }, {
    icon: <ThermometerSnowflakeIcon className="w-6 h-6" data-id="element-204" />,
    title: 'N+1 Cooling',
    description: 'Precision environmental controls maintaining optimal temperature and humidity.'
  }, {
    icon: <ShieldIcon className="w-6 h-6" data-id="element-205" />,
    title: '24/7 Security',
    description: 'On-site security personnel, CCTV surveillance, and strict access protocols.'
  }, {
    icon: <FingerprintIcon className="w-6 h-6" data-id="element-206" />,
    title: 'Biometric Access',
    description: 'Multi-factor authentication including biometric scanners for all secure areas.'
  }];
  return <section id="data-center" className="py-24 lg:py-32 bg-section-fade border-y border-border-color" data-id="element-207">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-208">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-209">
          {/* Content */}
          <div data-id="element-210">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ucensys-card border border-border-color mb-6" data-id="element-211">
              <span className="text-xs font-mono text-ucensys-teal uppercase tracking-wider" data-id="element-212">
                Our Facility
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6" data-id="element-213">
              Atlanta's Premier Data Center Facility
            </h2>
            <p className="text-xl font-body text-ucensys-teal mb-6" data-id="element-214">
              Located in Downtown Atlanta's Historic Financial District
            </p>
            <p className="text-text-secondary font-body leading-relaxed mb-10" data-id="element-215">
              Housed in the historic Truist Bank building, our flagship facility
              combines the impenetrable physical security of a legacy financial
              institution with state-of-the-art, high-density infrastructure
              required for modern AI and cloud workloads.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10" data-id="element-216">
              {features.map((feature, index) => <div key={index} className="flex gap-4" data-id="element-217">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-ucensys-navy border border-border-color flex items-center justify-center text-ucensys-teal" data-id="element-218">
                    {feature.icon}
                  </div>
                  <div data-id="element-219">
                    <h4 className="text-white font-heading font-semibold mb-1" data-id="element-220">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-text-muted font-body leading-relaxed" data-id="element-221">
                      {feature.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <a href="#contact" className="inline-flex px-8 py-4 rounded-full border-2 border-ucensys-teal text-ucensys-teal font-heading font-semibold hover:bg-ucensys-teal hover:text-white transition-all duration-300" data-id="element-222">
              Schedule a Facility Tour
            </a>
          </div>

          {/* Visual Placeholder */}
          <div className="relative h-[600px] rounded-2xl border border-border-color bg-ucensys-card overflow-hidden group" data-id="element-223">
            <div className="absolute inset-0 bg-circuit-pattern opacity-50" data-id="element-224"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-ucensys-teal/10 to-transparent" data-id="element-225"></div>

            {/* Abstract Server Rack Visualization */}
            <div className="absolute inset-0 flex items-center justify-center p-12" data-id="element-226">
              <div className="w-full h-full max-w-md flex flex-col gap-4" data-id="element-227">
                {[1, 2, 3, 4, 5, 6].map(rack => <div key={rack} className="w-full h-16 border border-border-light bg-ucensys-navy rounded flex items-center px-6 gap-4 relative overflow-hidden" data-id="element-228">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-ucensys-teal" data-id="element-229"></div>
                    <div className="flex gap-2" data-id="element-230">
                      <div className={`w-2 h-2 rounded-full ${rack % 2 === 0 ? 'bg-ucensys-teal animate-pulse' : 'bg-success'}`} data-id="element-231"></div>
                      <div className="w-2 h-2 rounded-full bg-success" data-id="element-232"></div>
                      <div className={`w-2 h-2 rounded-full ${rack === 3 ? 'bg-warning' : 'bg-success'}`} data-id="element-233"></div>
                    </div>
                    <div className="flex-1" data-id="element-234"></div>
                    <div className="flex gap-1" data-id="element-235">
                      {[...Array(12)].map((_, i) => <div key={i} className="w-1 h-6 bg-border-light rounded-sm" data-id="element-236"></div>)}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Location Overlay */}
            <div className="absolute bottom-6 left-6 right-6 bg-ucensys-navy/90 backdrop-blur border border-border-color p-4 rounded-xl flex items-center gap-4" data-id="element-237">
              <div className="w-10 h-10 rounded-full bg-ucensys-teal/20 flex items-center justify-center text-ucensys-teal" data-id="element-238">
                <MapPinIcon className="w-5 h-5" data-id="element-239" />
              </div>
              <div data-id="element-240">
                <h4 className="text-white font-heading font-semibold text-sm" data-id="element-241">
                  Downtown Atlanta
                </h4>
                <p className="text-text-muted font-body text-xs" data-id="element-242">
                  Historic Financial District
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}